import { useState, useEffect } from "react";
import { getZonesData } from "../../../log/api";
import { searchAssets } from "../../../../utils/API/Assets/Assets";
import GoogleMapComponent from "../../../../components/Maps/GoogleMapComponent";
import Grid from "@mui/material/Grid2";
import MaterialUiButton from "../../../../components/Buttons/MaterialUiButton/MaterialUiButton";
import MVPAssetsTable from "../../../mvp/table/MVPAssetsTable";
import PopUpZoneContent from "../../../../components/Maps/PopUpZoneContent";
import IndoorMapTable from "./IndoorMapTable";

export default function IndoorMap(props) {
  const {
    activeFacility,
    apiUrl,
    convertPayloadToMarkers,
    facilityId,
    floorPlanData = {},
    handleMarkerClick,
    initialAssets = [],
    isAssetHistory = false,
    mapSettings = {},
    organizationId,
    organizationsZonesMap = {},
    origin = null,
    polygonsToRender = [],
    popupContent = null,
    selectedAsset = null,
    setActiveMarker,
    setMapSettings = () => {},
    setParentState,
    setShowGoogleMap = () => {},
    showGoogleMap = false,
    timeZone,
    token,
    zones = [],
  } = props;
  const [state, setState] = useState({
    assets: [],
    zones: [],
    zoneCountHashMap: {},
  });

  const [highlightedZone, setHighlightedZone] = useState(null);
  const [highlightedAsset, setHighlightedAsset] = useState(null);

  const circlesToRender = Object.values(zones)
    .filter(
      (zone) =>
        zone.zoneType === "processing" &&
        zone.facilityId === facilityId &&
        //and also has a radius that is a valid number and has a number under xyz.x and xyz.y
        zone.radius &&
        !isNaN(zone.radius) &&
        zone.xyz &&
        !isNaN(zone.xyz.x) &&
        !isNaN(zone.xyz.y)
    )
    .map((zone) => ({
      ...zone,
      count: state.zoneCountHashMap[zone.zoneId] || 0,
    }));

  // Highlight the zone and asset when an asset is selected
  useEffect(() => {
    if (selectedAsset) {
      const { zone } = selectedAsset;
      const zoneId = zone?.zoneId;

      setHighlightedZone(zoneId);
      setHighlightedAsset(selectedAsset);
    }
  }, [selectedAsset]);

  // Fetch assets when the component mounts
  useEffect(() => {
    // Grab assets by facility
    const fetchAssets = async () => {
      try {
        const response = await searchAssets(
          {
            facilityId,
            organizationId,
            isInventory: true,
            token,
          },
          { filters: { limit: 2500 } }
        );

        const assets = response.assets || [];
        const zoneCountHashMap = {};

        assets.forEach((asset) => {
          const zoneId = asset?.zone?.zoneId;
          if (zoneId) {
            zoneCountHashMap[zoneId] = (zoneCountHashMap[zoneId] || 0) + 1;
          }
        });

        setState((prevState) => ({
          ...prevState,
          assets: response.assets || [],
          zoneCountHashMap,
        }));
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    };

    // also fetch zones data if facilityId is present
    const fetchZonesData = async () => {
      try {
        const response = await getZonesData({ token, apiUrl });
        //filter through zones and only get the ones with a zoneType of "target"
        const targetZones = response.zones.filter(
          (zone) => zone.zoneType === "target" && zone.facilityId === facilityId
        );
        setState((prevState) => ({
          ...prevState,
          // TODO: check resp to make sure key is zones
          zones: targetZones || [],
        }));
      } catch (error) {
        console.error("Error fetching zones:", error);
      }
    };

    if (facilityId) {
      fetchAssets();
      fetchZonesData();
    }
  }, [facilityId]);

  function handleCircleClick(zoneId) {
    setHighlightedZone(zoneId);
    setHighlightedAsset(null);
  }

  function handleRowClick(params) {
    const { row = {} } = params || {};
    const { zone } = row;
    const zoneId = zone?.zoneId;

    if (zone && zoneId) {
      // If zone is present, we want to highlight the zone and clear out any higlighted assets
      setHighlightedZone(zoneId);
      setHighlightedAsset(row);
    }
  }

  function handleOnFilterModelChange(newFilterModel) {
    const { quickFilterValues = [] } = newFilterModel || {};
    const newZoneCountHashMap = {};

    if (quickFilterValues.length > 0) {
      // Convert all search terms to lower case for case-insensitive search
      const searchTerms = quickFilterValues.map((term) => term.toLowerCase());

      const filteredAssets = state.assets.filter((asset) => {
        // Ensure asset.tag exists and is a string, then convert to lower case
        const assetTag = asset?.tag?.toLowerCase() || "";

        // Check if the asset's tag includes ANY of the search terms
        return searchTerms.some((term) => assetTag.includes(term));
      });

      // Recalculate the zone counts based on the filtered assets
      filteredAssets.forEach((asset) => {
        const zoneId = asset?.zone?.zoneId;
        if (zoneId) {
          newZoneCountHashMap[zoneId] = (newZoneCountHashMap[zoneId] || 0) + 1;
        }
      });
    } else {
      // If the filter is cleared, recalculate counts based on all assets
      state.assets.forEach((asset) => {
        const zoneId = asset?.zone?.zoneId;
        if (zoneId) {
          newZoneCountHashMap[zoneId] = (newZoneCountHashMap[zoneId] || 0) + 1;
        }
      });
    }

    // Update the state with the new hash map
    setState((prevState) => ({
      ...prevState,
      zoneCountHashMap: newZoneCountHashMap,
    }));
  }

  return (
    <Grid className="custom-css" container>
      {/* Back to GPS button */}
      <Grid item size={12}>
        {!showGoogleMap && (
          <MaterialUiButton
            color="primary"
            label="Back to GPS View"
            onClick={() => {
              setShowGoogleMap(true);

              //makes sure the map is reset to the original state so initialAssets/markers are shown or history page if that was most recent origin
              setParentState((prevState) => ({
                ...prevState,
                assets: initialAssets,
                markersToDisplay: convertPayloadToMarkers(initialAssets),

                assetHistories: prevState.assetHistories || [],
                assetHistoryMarkers: prevState.assetHistoryMarkers || [],
                isAssetHistory: prevState.isAssetHistory || false,
                // selectedAssetId: '',
              }));

              setActiveMarker(null);
            }}
            size="small"
            variant="text"
          />
        )}
      </Grid>

      {/* Indoor View */}
      <Grid
        item
        size={12}
        sx={{
          minHeight: "60vh",
        }}
      >
        <GoogleMapComponent
          circlesToRender={circlesToRender}
          floorPlanData={floorPlanData.find((floorPlan) => {
            return floorPlan.facility.facilityId === activeFacility;
          })}
          handleMarkerClick={handleMarkerClick}
          highlightedZone={highlightedZone}
          mapSettings={mapSettings}
          mapTypeId="satellite"
          markerKey={isAssetHistory ? "assetHistoryId" : "assetId"}
          onCircleClick={handleCircleClick}
          onCircleInfoWindowClose={() => setHighlightedZone(null)}
          origin={origin}
          polygons={polygonsToRender}
          popUpContent={popupContent}
          popUpZoneContent={(clickedZoneData) => (
            <PopUpZoneContent zone={clickedZoneData} asset={highlightedAsset} />
          )}
          setActiveMarker={setActiveMarker}
          setMapSettings={setMapSettings}
        />
      </Grid>

      {/* Clear Selection Button */}
      <Grid item size={12}>
        <MaterialUiButton
          color="primary"
          label="Clear Selection"
          onClick={() => {
            setHighlightedZone(null);
            setHighlightedAsset(null);
          }}
          size="small"
          variant="text"
        />
      </Grid>

      {/* MVPAssetsTable component */}
      <Grid item size={12}>
        {/* <IndoorMapTable 
          {...props}
          zones={organizationsZonesMap}
          highlightedZone={highlightedZone}
        /> */}
        <MVPAssetsTable
          activeFacility={activeFacility}
          handleRowClick={handleRowClick}
          highlightedZone={highlightedZone}
          onFilterModelChange={handleOnFilterModelChange}
          state={state}
          timeZone={timeZone}
          zones={organizationsZonesMap}
        />
      </Grid>
    </Grid>
  );
}
